import { formatInstant } from 'scenes/RouteReport/utils/time';

import {
  TiendaPerfectaVisitHistoryNamed,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import {
  AggregatedExecution,
  AggregatedStops,
  VisitSelection,
} from './selection';
import { FALLBACK_VAN_COLOR } from './stateColors';

const round1 = (n: number): number => Math.round(n * 10) / 10;

export interface VansMetrics {
  planned: number;
  executed: number;
  incidents: number;
  /** Incidents over planned, one decimal. */
  incidentPct: number;
  pending: number;
  compliancePct: number;
  materialUnits: number;
  /** `HH:mm` in the user's time zone. */
  avgStart: string | null;
  avgEnd: string | null;
  avgDurationHours: number | null;
  visitsPerHour: number | null;
}

interface BaseRow extends VansMetrics {
  key: string;
  vanId: string;
  routes: number;
  days: number;
}

export interface VanTableRow extends BaseRow {
  kind: 'van';
  name: string;
  color: string;
  zones: TiendaPerfectaVisitHistoryNamed[];
  children: ExecutionTableRow[];
}

export interface ExecutionTableRow extends BaseRow {
  kind: 'execution';
  /** The route code. */
  name: string;
  routeId: string;
  date: string;
  zones: TiendaPerfectaVisitHistoryNamed[];
}

export type VansTableRow = VanTableRow | ExecutionTableRow;

export interface VansTotalRow extends VansMetrics {
  routes: number;
  days: number;
  zonesCount: number;
}

const incidentPctOf = (incidents: number, planned: number): number =>
  planned > 0 ? round1((incidents / planned) * 100) : 0;

const hoursOf = (minutes: number | null): number | null =>
  minutes === null ? null : round1(minutes / 60);

const metricsOf = (
  e: Pick<
    AggregatedExecution,
    | 'planned'
    | 'executed'
    | 'incidents'
    | 'pending'
    | 'compliancePct'
    | 'materialUnits'
    | 'visitsPerHour'
  > & { durationMinutes: number | null },
  avgStart: string | null,
  avgEnd: string | null
): VansMetrics => ({
  planned: e.planned,
  executed: e.executed,
  incidents: e.incidents,
  incidentPct: incidentPctOf(e.incidents, e.planned),
  pending: e.pending,
  compliancePct: e.compliancePct,
  materialUnits: e.materialUnits,
  avgStart,
  avgEnd,
  avgDurationHours: hoursOf(e.durationMinutes),
  visitsPerHour: e.visitsPerHour,
});

/**
 * One row per van PRESENT IN THE STOPS, with its executions as children. Every
 * number comes from `aggregated` (`aggregateStops` over the flat stops), so van
 * rows, execution rows and the total row share one derivation and one time
 * zone: the user's. `summary.vans` only gives the ORDER (listed vans first,
 * then the rest by name) and a name fallback: the server sends `HH:mm` in the
 * company zone and per-execution counts the final contract does not carry, and
 * mixing sources would make the rows impossible to compare. A van listed by
 * the summary but with no stops has nothing to show and gets no row. Van and
 * total averages are over executions, never an average of averages.
 */
export const buildVanRows = (
  summary: TiendaPerfectaVisitHistorySummary,
  aggregated: AggregatedStops,
  colors: Map<string, string>,
  timeZone: string
): VanTableRow[] => {
  const listed = summary.vans.map((row) => row.van);
  const order = new Map(listed.map((van, index) => [van.id, index]));
  const summaryName = new Map(listed.map((van) => [van.id, van.name]));
  const rank = (vanId: string) => order.get(vanId) ?? listed.length;

  // `Array.sort` is stable: vans the summary does not list keep the name order
  // `aggregateStops` gave them.
  return [...aggregated.vans]
    .sort((a, b) => rank(a.van.id) - rank(b.van.id))
    .map((van): VanTableRow => {
      const vanId = van.van.id;

      return {
        kind: 'van',
        key: vanId,
        vanId,
        name: van.van.name || summaryName.get(vanId) || '',
        color: colors.get(vanId) ?? FALLBACK_VAN_COLOR,
        zones: van.zones,
        routes: van.routes,
        days: van.days,
        ...metricsOf(
          { ...van, durationMinutes: van.avgDurationMinutes },
          van.avgStartTime,
          van.avgEndTime
        ),
        children: van.executions.map(
          (e): ExecutionTableRow => ({
            kind: 'execution',
            key: `${vanId}|${e.routeId}|${e.date}`,
            vanId,
            name: e.routeCode,
            routeId: e.routeId,
            date: e.date,
            zones: [e.zone],
            routes: 1,
            days: 1,
            ...metricsOf(
              e,
              e.startedAt
                ? formatInstant(e.startedAt, timeZone, 'HH:mm')
                : null,
              e.finishedAt
                ? formatInstant(e.finishedAt, timeZone, 'HH:mm')
                : null
            ),
          })
        ),
      };
    });
};

/**
 * The total row, derived from the same `aggregated` as the van rows, so it is
 * always their sum: counts, compliance (total executed over total planned) and
 * the time metrics averaged over ALL executions of all vans. Executions with
 * no started stop only leave the time metrics, never the counts.
 */
export const buildTotalRow = (aggregated: AggregatedStops): VansTotalRow => ({
  ...metricsOf(
    {
      ...aggregated.total,
      durationMinutes: aggregated.total.avgDurationMinutes,
    },
    aggregated.total.avgStartTime,
    aggregated.total.avgEndTime
  ),
  routes: aggregated.total.routes,
  days: aggregated.total.days,
  zonesCount: new Set(aggregated.executions.map((e) => e.zone.id)).size,
});

/** Whether a row belongs to the shared selection (otherwise it dims). */
export const rowMatchesSelection = (
  row: VansTableRow,
  selection: VisitSelection
): boolean => {
  if (!selection) return true;

  if (row.kind === 'execution') {
    switch (selection.kind) {
      case 'day':
        return row.date === selection.date;
      case 'route':
        return row.routeId === selection.routeId && row.date === selection.date;
      case 'van':
        return row.vanId === selection.vanId;
      case 'zone':
        return row.zones.some((z) => z.id === selection.zoneId);
    }
  }

  switch (selection.kind) {
    case 'van':
      return row.vanId === selection.vanId;
    case 'zone':
      return row.zones.some((z) => z.id === selection.zoneId);
    default:
      return row.children.some((child) =>
        rowMatchesSelection(child, selection)
      );
  }
};
